// src/pages/Offers.js
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header  from "../components/Header";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import "../styles/offers.css";
import { edtTime } from "../utils/edt";

const API = process.env.REACT_APP_API_URL || "https://ipanel.idigitalhub.org/api";
const POLL_MS = 15 * 1000;

/* ─── Sort Icon ─────────────────────────────── */
function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <span style={{opacity:0.3,marginLeft:3}}>↕</span>;
  return <span style={{marginLeft:3,color:"var(--sidebar-active-color)"}}>{sortDir === "asc" ? "↑" : "↓"}</span>;
}

/* ─── Status Badge ──────────────────────────── */
function StatusBadge({ value }) {
  if (!value) return <span className="ofr-badge ofr-badge-grey">—</span>;
  const lower = String(value).toLowerCase();
  let cls = "ofr-badge-grey";
  if (lower === "approved" || lower === "active" || lower === "public")           cls = "ofr-badge-green";
  else if (lower === "pending" || lower === "apply to run" || lower === "require_approval") cls = "ofr-badge-amber";
  else if (lower === "rejected" || lower === "private")                            cls = "ofr-badge-red";
  else if (lower === "paused" || lower === "inactive" || lower === "deleted")     cls = "ofr-badge-grey";
  return <span className={`ofr-badge ${cls}`}>{value}</span>;
}

function shouldShowApply(status) {
  if (!status) return false;
  const lower = String(status).toLowerCase();
  return lower === "pending"
      || lower === "apply to run"
      || lower === "require_approval";
}

/* ─── Multi-select dropdown ─────────────────── */
function MultiSelect({ label, options, selected, onChange, placeholder = "All" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = key => {
    if (selected.includes(key)) onChange(selected.filter(k => k !== key));
    else                        onChange([...selected, key]);
  };

  const display = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (options.find(o => o.key === selected[0])?.label || selected[0])
      : `${selected.length} selected`;

  return (
    <div className="ofr-msel-wrap" ref={ref}>
      <button className="ofr-msel-btn" onClick={() => setOpen(!open)}>
        <span className="ofr-msel-lbl">{label}:</span>
        <span className="ofr-msel-val">{display}</span>
        <span style={{fontSize:10,opacity:0.6,marginLeft:4}}>▾</span>
      </button>
      {open && (
        <div className="ofr-msel-dd">
          {options.length === 0 && <div className="ofr-msel-empty">No options</div>}
          {options.map(o => (
            <label key={o.key} className="ofr-msel-item">
              <input type="checkbox"
                checked={selected.includes(o.key)}
                onChange={() => toggle(o.key)}/>
              <span>{o.label}</span>
              {o.count != null && <span className="ofr-msel-count">{o.count}</span>}
            </label>
          ))}
          {selected.length > 0 && (
            <div className="ofr-msel-clear" onClick={() => onChange([])}>Clear all</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Numbered pagination ─── */
function buildPageNumbers(currentPage, totalPages) {
  const maxVisible = 5;
  if (totalPages <= maxVisible) return Array.from({length: totalPages}, (_, i) => i + 1);
  let start = Math.max(1, currentPage - 2);
  let end   = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
  const out = [];
  for (let p = start; p <= end; p++) out.push(p);
  return out;
}

const PG_OPTIONS = [25, 50, 100, 200];

export default function Offers() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [search,        setSearch]        = useState("");
  const [affiliateSel,  setAffiliateSel]  = useState([]);
  const [statusSel,     setStatusSel]     = useState([]);
  const [categorySel,   setCategorySel]   = useState([]);

  const [sortCol,    setSortCol]    = useState("priority");
  const [sortDir,    setSortDir]    = useState("asc");
  const [pageSize,   setPageSize]   = useState(100);
  const [page,       setPage]       = useState(1);

  const [offers,     setOffers]     = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [lastSync,   setLastSync]   = useState(null);

  const [filterOpts, setFilterOpts] = useState({ affiliates: [], statuses: [], categories: [] });

  const pollTimer  = useRef(null);
  const lastRunRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/offers/filters`)
      .then(r => setFilterOpts({
        affiliates: (r.data.affiliates || []).map(a => ({ key: String(a.id), label: a.name, count: a.offer_count })),
        statuses:   (r.data.statuses   || []).map(s => ({ key: s.status,        label: s.status,        count: s.count })),
        categories: (r.data.categories || []).map(c => ({ key: c.category_name, label: c.category_name, count: c.count })),
      }))
      .catch(() => {});
  }, []);

  const fetchOffers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const params = { page, pageSize, sortBy: sortCol, sortDir };
      if (search.trim())        params.search = search.trim();
      if (affiliateSel.length)  params.affiliate_id = affiliateSel.join(",");
      if (statusSel.length)     params.status       = statusSel.join(",");
      if (categorySel.length)   params.category     = categorySel.join(",");

      const res = await axios.get(`${API}/offers`, { params });
      setOffers(res.data.offers || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 0);
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || "Failed to fetch offers.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, pageSize, sortCol, sortDir, search, affiliateSel, statusSel, categorySel]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchOffers(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => { fetchOffers(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, sortCol, sortDir, affiliateSel, statusSel, categorySel]);

  useEffect(() => {
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/offers/sync/last-run`);
        const ts  = res.data?.jobs?.["offers-list-sync"]?.last_run_at;
        if (ts && ts !== lastRunRef.current) {
          lastRunRef.current = ts;
          setLastSync(new Date(ts));
          fetchOffers(true);
        }
      } catch (e) { /* */ }
    };
    poll();
    pollTimer.current = setInterval(poll, POLL_MS);
    return () => clearInterval(pollTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir(col === "priority" ? "asc" : "desc"); }
    setPage(1);
  };

  const handleOfferNameClick = (row) => {
    navigate(`/offers/${row.affiliate_id}/${encodeURIComponent(row.offer_external_id)}`);
  };
  const handleApply = (row) => {
    if (row.affiliate_portal_url) {
      window.open(row.affiliate_portal_url, "_blank", "noopener,noreferrer");
    }
  };
  const fmtPayout = (row) => {
    if (!row.payout_type) return "—";
    if (row.payout_type === "RevShare") {
      if (row.payout_percentage == null) return "RevShare —";
      return `RevShare ${parseFloat(row.payout_percentage).toFixed(2)}%`;
    }
    if (row.payout_amount == null) return `${row.payout_type} —`;
    return `${row.payout_type} $${parseFloat(row.payout_amount).toFixed(2)}`;
  };

  const activeFilters = [
    ...affiliateSel.map(id => ({ kind: "affiliate", key: id, label: filterOpts.affiliates.find(a => a.key === id)?.label || id })),
    ...statusSel.map(s   => ({ kind: "status",    key: s,  label: s })),
    ...categorySel.map(c => ({ kind: "category",  key: c,  label: c })),
  ];
  const removeFilter = (f) => {
    if (f.kind === "affiliate") setAffiliateSel(s => s.filter(x => x !== f.key));
    if (f.kind === "status")    setStatusSel(s    => s.filter(x => x !== f.key));
    if (f.kind === "category")  setCategorySel(s  => s.filter(x => x !== f.key));
  };
  const clearAllFilters = () => {
    setAffiliateSel([]); setStatusSel([]); setCategorySel([]); setSearch("");
  };

  const headerSubtitle = lastSync
    ? `Synced: ${edtTime(lastSync)} EDT`
    : "Waiting for sync...";

  const pageNumbers = totalPages > 1 ? buildPageNumbers(page, totalPages) : [];

  return (
    <div className="layout">
      <Sidebar active="offers"/>
      <div className="layout-main ofr-page">
        <Header title="Offers" subtitle={headerSubtitle} userName={user?.name}/>

        <div className="content ofr-content">
          {/* Filter Toolbar */}
          <div className="ofr-toolbar">
            <div className="ofr-toolbar-row1">
              <div className="ofr-search">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input type="text"
                  placeholder="Search by offer name or ID..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}/>
                {search && (<button onClick={() => setSearch("")} className="ofr-search-clear">✕</button>)}
              </div>

              <MultiSelect label="Affiliate" options={filterOpts.affiliates} selected={affiliateSel}
                onChange={(v) => { setAffiliateSel(v); setPage(1); }} placeholder="All"/>
              <MultiSelect label="Status" options={filterOpts.statuses} selected={statusSel}
                onChange={(v) => { setStatusSel(v); setPage(1); }} placeholder="All"/>
              <MultiSelect label="Category" options={filterOpts.categories} selected={categorySel}
                onChange={(v) => { setCategorySel(v); setPage(1); }} placeholder="All"/>
            </div>

            {activeFilters.length > 0 && (
              <div className="ofr-toolbar-row2">
                <span className="ofr-filter-lbl">Active:</span>
                {activeFilters.map((f, i) => (
                  <span key={i} className="ofr-pill">
                    {f.label}
                    <button onClick={() => removeFilter(f)} className="ofr-pill-x">✕</button>
                  </span>
                ))}
                <button className="ofr-clear-all" onClick={clearAllFilters}>Clear all</button>
              </div>
            )}
          </div>

          {error && <div className="ofr-error">⚠ {error}</div>}

          {/* Table — Everflow architecture: internal scroll container */}
          <div className="ofr-table-wrap">
            <div className="ofr-table-head">
              <span className="ofr-count">{loading ? "Loading..." : `${total.toLocaleString()} offers`}</span>
              <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text-muted)"}}>
                <span>Show</span>
                <select className="pg-size-sel" value={pageSize}
                  onChange={e => { setPageSize(parseInt(e.target.value)); setPage(1); }}>
                  {PG_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span>per page</span>
              </div>
            </div>

            {/* THE SCROLL AREA — this is the scroll container.
                The thead inside sticks to the TOP of THIS div, not the window.
                This is exactly how Everflow does it. */}
            <div className="ofr-scroll-area">
              <table className="ofr-table">
                <thead>
                  <tr>
                    <th className="sortable th-aff" onClick={() => handleSort("offer_external_id")}>
                      Affiliate / ID<SortIcon col="offer_external_id" sortCol={sortCol} sortDir={sortDir}/>
                    </th>
                    <th className="sortable th-offer" onClick={() => handleSort("offer_name")}>
                      Offer Name<SortIcon col="offer_name" sortCol={sortCol} sortDir={sortDir}/>
                    </th>
                    <th className="sortable th-cat" onClick={() => handleSort("category_name")}>
                      Category<SortIcon col="category_name" sortCol={sortCol} sortDir={sortDir}/>
                    </th>
                    <th className="sortable th-pay th-right" onClick={() => handleSort("payout_amount")}>
                      Payout<SortIcon col="payout_amount" sortCol={sortCol} sortDir={sortDir}/>
                    </th>
                    <th className="th-cou">Countries</th>
                    <th className="sortable th-status-action" onClick={() => handleSort("status")}>
                      Status<SortIcon col="status" sortCol={sortCol} sortDir={sortDir}/>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="td-loading"><div className="loading-spinner"/>Fetching offers...</td></tr>
                  ) : offers.length === 0 ? (
                    <tr><td colSpan={6} className="td-empty">No offers found. Try adjusting filters.</td></tr>
                  ) : (
                    offers.map((r) => {
                      const showApply = shouldShowApply(r.status) && r.affiliate_portal_url;
                      return (
                        <tr key={r.id}>
                          <td>
                            <div className="td-aff-name">{r.affiliate_name}</div>
                            <div className="td-aff-id">#{r.offer_external_id}</div>
                          </td>
                          <td className="td-offer">
                            <span className="ofr-offer-link"
                              onClick={() => handleOfferNameClick(r)}
                              title={r.offer_name}>
                              {r.offer_name}
                            </span>
                          </td>
                          <td>{r.category_name || <span className="td-dash">—</span>}</td>
                          <td className="td-payout">{fmtPayout(r)}</td>
                          <td className="td-countries">
                            {r.allowed_countries
                              ? <span title={r.allowed_countries}>{r.allowed_countries.split(",").slice(0,3).join(", ")}{r.allowed_countries.split(",").length > 3 ? "..." : ""}</span>
                              : <span className="td-dash">—</span>}
                          </td>
                          <td className="td-status-action">
                            <StatusBadge value={r.status}/>
                            {showApply && (
                              <button className="ofr-btn ofr-btn-apply"
                                onClick={() => handleApply(r)}
                                title={`Apply on ${r.affiliate_name}`}>
                                Apply →
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination — INSIDE the table card, NOT fixed-position */}
            {totalPages > 1 && (
              <div className="ofr-pagination">
                <span className="pg-info">{total.toLocaleString()} Total</span>
                <div className="pg-btns">
                  <button className="pg-btn" disabled={page === 1} onClick={() => setPage(1)} title="First page">«</button>
                  <button className="pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous page">‹</button>
                  {pageNumbers.map(p => (
                    <button key={p} className={`pg-btn ${p === page ? "active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button className="pg-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next page">›</button>
                  <button className="pg-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last page">»</button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
